import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getProject } from '@hub/helpers/get-project';
import { useStore } from '@hub/helpers/use-store';
import { Button, Flex, TextComponent } from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import { ConsistencyRules } from '@monorepo/types';
import ProfitableDaysModal from '../../dashboard-common/account-consistency-rules/profitable-days-modal/profitable-days-modal';
import ProfitableDaysRule from '../account-rules/profitable-days/profitable-days-rule';
import ProfitableDaysRuleTTPCard from '../account-rules/profitable-days-ttp/profitable-days-rule-ttp';
import ProfitableDaysProgress from '../progress-cards-5ers/profitable-days-progress';
import { DashboardCardType } from './inactive-days';

interface Props {
  accountId: string;
  programId: string;
  type: DashboardCardType;
}

const ProfitableDays: FunctionComponent<Props> = ({
  accountId,
  programId,
  type,
}) => {
  const {
    dataStore: { accountStore, programStore },
  } = useStore();

  const [profitableDays, setProfitableDays] = useState<Date[]>([]);
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Initial);
  const { t } = useTranslation(['dashboardTemplates']);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const isLoading = loadingState === LoadingState.Loading;

  const { isTtp } = getProject();

  const consistencyRules =
    programStore.get(programId)?.config?.consistencyRules;

  const profitableDaysTarget = consistencyRules?.find(
    (rule) => rule.rule === ConsistencyRules.ProfitableDays,
  )?.value;

  useEffect(() => {
    try {
      updateLoadingState(LoadingState.Loading);
      accountStore
        .getConsistencyRule(accountId, ConsistencyRules.ProfitableDays)
        .then((result) => {
          const { profitableDays } = result;

          if (profitableDays) {
            setProfitableDays(profitableDays);
          }
        });
    } catch (error) {
      console.error(`Failed getting account consistency rules`, error);
    } finally {
      updateLoadingState(LoadingState.Loaded);
    }
  }, []);

  if (!profitableDaysTarget || !profitableDays) {
    return null;
  }

  const currentProfitableDays = profitableDays.length;
  const hasProfitableDays = currentProfitableDays > 0;
  const tooltipLabel = t(
    'dashboardTemplates:minimum_profit_for_profitable_day',
  );

  const TooltipText = (
    <Flex vertical gap={15}>
      <TextComponent level={7}>{tooltipLabel}</TextComponent>
      {hasProfitableDays && (
        <Button type="default" size="small">
          {t('dashboardTemplates:show_profitable_days')}
        </Button>
      )}
    </Flex>
  );
  const openModal = () => {
    if (hasProfitableDays) {
      setModalOpen(true);
    }
  };
  const ProfitableDaysCard =
    type === DashboardCardType.Rule
      ? ProfitableDaysRule
      : ProfitableDaysProgress;

  return (
    <>
      <ProfitableDaysModal
        accountId={accountId}
        close={() => setModalOpen(false)}
        open={modalOpen}
      />
      {isTtp ? (
        <ProfitableDaysRuleTTPCard
          openModal={openModal}
          currentProfitableDays={currentProfitableDays}
          profitableDaysTarget={profitableDaysTarget}
          isLoading={isLoading}
        />
      ) : (
        <ProfitableDaysCard
          openModal={openModal}
          currentProfitableDays={currentProfitableDays}
          profitableDaysTarget={profitableDaysTarget}
          tooltipText={TooltipText}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default ProfitableDays;
